import { Elements, type HeroOverlayCardProps } from '@/components/elements';
import Image, { type StaticImageData } from 'next/image';
import clsx from 'clsx';

/** Matches `Elements.Description` `type` prop. */
export type UseCasesHeroDescriptionType =
    | 'default'
    | 'small'
    | 'medium'
    | 'large'
    | 'extraSm';

const descriptionColumnClass =
    'max-w-[600px] text-left text-gray-900 md:w-full md:max-w-none';

export type UseCasesHeroBadge = {
    iconSrc: string;
    label: string;
    iconAlt?: string;
};

export type UseCasesHeroImage = {
    src: string | StaticImageData;
    alt: string;
    width?: number;
    height?: number;
    sizes?: string;
    priority?: boolean;
    className?: string;
    /**
     * CSS `object-position` for the hero image (with `object-cover`), e.g. `center top`,
     * `50% 30%`, `right center`. Set per page so each photo crops correctly.
     */
    objectPosition?: string;
};

export type UseCasesHeroProps = {
    badge: UseCasesHeroBadge;
    title: string;
    description: string;
    /** Optional second paragraph below the first description. */
    secondaryDescription?: string;
    secondaryDescriptionClassName?: string;
    secondaryDescriptionType?: UseCasesHeroDescriptionType;
    image: UseCasesHeroImage;
    overlayCards: HeroOverlayCardProps[];
    className?: string;
    /**
     * Tailwind classes for the overlay cards wrapper (`absolute` stack on the hero image).
     * Merged after defaults so you can override position, e.g. `bottom-6 right-2 md:!-translate-x-32`.
     */
    overlayCardsPositionClassName?: string;
};

/** Display size for the hero image (all use-case sections). */
const HERO_IMAGE_PX = 471;

const defaultImageSizes = `(min-width: 1024px) ${HERO_IMAGE_PX}px, (min-width: 768px) 100vw, min(100vw, ${HERO_IMAGE_PX}px)`;

export const UseCasesHero = ({
    badge,
    title,
    description,
    secondaryDescription,
    secondaryDescriptionClassName,
    secondaryDescriptionType = 'medium',
    image,
    overlayCards,
    className,
    overlayCardsPositionClassName,
}: UseCasesHeroProps) => {
    const {
        src: imageSrc,
        alt: imageAlt,
        sizes: imageSizes = defaultImageSizes,
        priority: imagePriority = false,
        className: imageClassName,
        objectPosition: imageObjectPosition,
    } = image;

    return (
        <section
            className={clsx('overflow-hidden px-5 pt-10 pb-20', className)}
        >
            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
                <div className="flex w-full flex-col items-stretch gap-8 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex w-full flex-col items-start gap-4 lg:max-w-[600px]">
                        <div className="flex items-center justify-start gap-2 rounded-lg bg-brand-100 px-2 py-1">
                            <Image
                                src={badge.iconSrc}
                                alt={badge.iconAlt ?? ''}
                                width={20}
                                height={20}
                                className="h-5 w-5 shrink-0 object-contain object-center text-brand-700"
                            />
                            <p className="text-base font-normal text-brand-700">
                                {badge.label}
                            </p>
                        </div>
                        <Elements.Title
                            title={title}
                            type="h1"
                            className="max-w-[600px] text-left font-semibold text-brand-700"
                        />
                        <Elements.Description
                            description={description}
                            type="medium"
                            className={descriptionColumnClass}
                        />
                        {secondaryDescription ? (
                            <Elements.Description
                                description={secondaryDescription}
                                type={secondaryDescriptionType}
                                className={clsx(
                                    descriptionColumnClass,
                                    secondaryDescriptionClassName,
                                )}
                            />
                        ) : null}
                    </div>

                    <div
                        className={clsx(
                            'relative isolate mx-auto min-w-0 h-[330px] w-full max-w-[471px] shrink-0 self-center',
                            'lg:aspect-square lg:mx-0 lg:h-auto lg:self-auto',
                        )}
                    >
                        <div className="relative z-0 h-full w-full overflow-hidden rounded-3xl shadow-[16px_16px_16px_rgba(0,0,0,0.15)]">
                            <Image
                                src={imageSrc}
                                alt={imageAlt}
                                fill
                                className={clsx(
                                    'min-h-full min-w-full object-cover',
                                    imageClassName,
                                )}
                                style={
                                    imageObjectPosition
                                        ? { objectPosition: imageObjectPosition }
                                        : undefined
                                }
                                sizes={imageSizes}
                                priority={imagePriority}
                            />
                        </div>

                        <div
                            className={clsx(
                                /* full width of hero image so card `w-full max-w-[…]` resolves */
                                'pointer-events-none absolute inset-x-0 bottom-3 z-20 flex min-w-0 flex-col items-end gap-2 px-3 sm:bottom-4 sm:px-4 md:translate-x-0 lg:-translate-x-80',
                                overlayCardsPositionClassName,
                            )}
                        >
                            {overlayCards.map((card, index) => (
                                <Elements.HeroOverlayCard
                                    key={`${card.title}-${index}`}
                                    {...card}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
